import type { UserSavedSearch } from "@/modules/jobAds/client/api/types"
import type { CurrentUserCurriculumIds, CurrentUserSearchCriteria, SearchConfigFilter } from "@/modules/user/api/types"

import _snakeCase from "lodash/snakeCase"
import { NIL as NIL_UUID } from "uuid"

import { ALGOLIA_FACETS, NOT_OPERATOR, NOT_OPERATOR_SUBSTITUTE } from "@/modules/jobAds/client/constants"
import { getAlgoliaPublicationDateFilter } from "@/modules/jobAds/client/utils/getAlgoliaPublicationDateFilter"
import { getEscapedAlgoliaValue } from "@/modules/jobAds/client/utils/getEscapedAlgoliaValue"
import { computeLocationObjectFromSingleQueryParam, getLocationType } from "@/modules/locations/locations"
import { logger } from "@/modules/monitoring/logger"

export type AlgoliaJobAdsRequestParameters = {
  candidacyTypes?: string[]
  ccPositionCategoryIds?: string[]
  ccShortName?: string
  companyBusinessTypes?: string[]
  companyIds?: string[]
  companySectors?: string[]
  contractDurations?: string[]
  contractTypes: string[]
  contractTypesSearchConfig?: SearchConfigFilter
  currentUserSearchCriteria?: CurrentUserSearchCriteria
  curriculumIds?: CurrentUserCurriculumIds
  excludedStudyLevels?: number[]
  exclusive?: string[]
  facultySchoolId?: string
  indexName: string
  isAbroadOnly: boolean
  isLogged: boolean
  jobWithImpact?: string
  lat: string | null
  lng: string | null
  locales: string[]
  location: string | null
  page: number
  positionCategories?: string[]
  publicationDate?: string | null
  radius?: number
  remoteTypes?: string[]
  savedSearch?: UserSavedSearch | null
  schoolId: string
  seenJobAds?: string[]
  startDates?: string[]
  studyLevels?: string[]
  studyLevelsSearchConfig?: SearchConfigFilter
  tags?: string[]
  userCountry?: string | null
  userQuery: string
  workExperiences?: string[]
}

export type FacetsWithValues = {
  CANDIDACY_TYPE: string[]
  CC_NETWORK: string[]
  COMPANY_BUSINESS_TYPE: string[]
  COMPANY_IDS: string[]
  COMPANY_SECTORS: string[]
  CONTRACT_DURATION: string[]
  CONTRACT_TYPE: string[]
  CURRICULUM_IDS: string[]
  EXCLUDED_STUDY_LEVELS: string[][]
  JOB_WITH_IMPACT: string[]
  LOCALE: string[]
  LOCATIONS: string[]
  POSITION_CATEGORY_UUID: string[]
  PUBLICATION_DATE: string[]
  REMOTE_TYPES: string[]
  SCHOOL_IDS: string[]
  SCORE: string[]
  START_DATE: string[]
  STUDY_LEVELS: string[]
  TAGS: string[][]
  WORK_EXPERIENCE_CODE: string[]
}

const createInitialFacetsFilters = (): FacetsWithValues => ({
  CANDIDACY_TYPE: [],
  CC_NETWORK: [],
  COMPANY_BUSINESS_TYPE: [],
  COMPANY_IDS: [],
  COMPANY_SECTORS: [],
  CONTRACT_DURATION: [],
  CONTRACT_TYPE: [],
  CURRICULUM_IDS: [],
  EXCLUDED_STUDY_LEVELS: [],
  JOB_WITH_IMPACT: [],
  LOCALE: [],
  LOCATIONS: [],
  POSITION_CATEGORY_UUID: [],
  PUBLICATION_DATE: [],
  REMOTE_TYPES: [],
  SCHOOL_IDS: [],
  SCORE: [],
  START_DATE: [],
  STUDY_LEVELS: [],
  TAGS: [],
  WORK_EXPERIENCE_CODE: [],
})

export const getAlgoliaFacetFiltersFromParams = (
  params: AlgoliaJobAdsRequestParameters,
  userCountry: string | null | undefined
): FacetsWithValues => {
  const facetsFilters = createInitialFacetsFilters()
  const {
    companySectors,
    candidacyTypes,
    companyBusinessTypes,
    companyIds,
    contractDurations,
    contractTypes,
    curriculumIds,
    excludedStudyLevels,
    exclusive,
    facultySchoolId,
    isAbroadOnly,
    jobWithImpact,
    locales,
    location,
    positionCategories,
    remoteTypes,
    schoolId,
    startDates,
    studyLevels,
    tags,
    workExperiences,
    publicationDate,
  } = params

  if (location) {
    const locationObject = computeLocationObjectFromSingleQueryParam(location)
    const locationType = getLocationType(locationObject)
    const hasLocationTextFilter = locationType !== "city"
    const locationValue = locationObject[locationType]
    if (hasLocationTextFilter && locationValue) {
      facetsFilters.LOCATIONS = [
        `${ALGOLIA_FACETS.LOCATIONS.replace("<location_type>", _snakeCase(locationType))}:'${getEscapedAlgoliaValue(
          locationValue
        )}'`,
      ]
    }
  }

  if (isAbroadOnly) {
    if (userCountry) {
      facetsFilters.LOCATIONS = [
        `${NOT_OPERATOR_SUBSTITUTE}${ALGOLIA_FACETS.LOCATIONS.replace("<location_type>", "country")}:'${userCountry}'`,
      ]
    } else {
      logger.error(
        "Trying to compute a Algolia request with abroad only filter active but unable to find the user's country."
      )
    }
  }

  if (candidacyTypes && candidacyTypes.length > 0) {
    facetsFilters.CANDIDACY_TYPE = candidacyTypes.map(
      candidacyType => `${ALGOLIA_FACETS.CANDIDACY_TYPE}:'${candidacyType}'`
    )
  }

  if (companySectors && companySectors.length > 0) {
    facetsFilters.COMPANY_SECTORS = companySectors.map(
      companySector => `${ALGOLIA_FACETS.COMPANY_SECTORS}:'${companySector}'`
    )
  }
  if (contractDurations && contractDurations.length > 0) {
    facetsFilters.CONTRACT_DURATION = contractDurations.map(
      duration => `${ALGOLIA_FACETS.CONTRACT_DURATION}:${duration}`
    )
  }
  if (contractTypes.length > 0) {
    facetsFilters.CONTRACT_TYPE = contractTypes.map(contractType => `${ALGOLIA_FACETS.CONTRACT_TYPE}:'${contractType}'`)
  }
  if (curriculumIds && curriculumIds.length > 0) {
    facetsFilters.CURRICULUM_IDS = curriculumIds.map(curriculum => `${ALGOLIA_FACETS.CURRICULUM_IDS}:'${curriculum}'`)
  }
  if (locales.length > 0) {
    facetsFilters.LOCALE = locales.map(locale => `${ALGOLIA_FACETS.LOCALE}:'${locale}'`)
  }
  if (positionCategories && positionCategories.length > 0) {
    facetsFilters.POSITION_CATEGORY_UUID = positionCategories.map(
      category => `${ALGOLIA_FACETS.POSITION_CATEGORY_UUID}:'${category}'`
    )
  }
  if (startDates && startDates.length > 0) {
    facetsFilters.START_DATE = startDates.map(startDate => `${ALGOLIA_FACETS.START_DATE}:'${startDate}'`)
  }
  if (studyLevels && studyLevels.length > 0) {
    facetsFilters.STUDY_LEVELS = studyLevels.map(studyLevel => `${ALGOLIA_FACETS.STUDY_LEVELS}:'${studyLevel}'`)
  }

  if (excludedStudyLevels && excludedStudyLevels.length > 0) {
    facetsFilters.EXCLUDED_STUDY_LEVELS = excludedStudyLevels.map(excludedStudyLevel => [
      `${NOT_OPERATOR} ${ALGOLIA_FACETS.STUDY_LEVELS}:${excludedStudyLevel}`,
    ])
  }

  // CC specific
  if (schoolId !== NIL_UUID) {
    facetsFilters.SCHOOL_IDS = [`${ALGOLIA_FACETS.SCHOOL_IDS}:'${schoolId}'`]

    if (exclusive && exclusive.length > 0) {
      exclusive.forEach(exclusiveFilter => {
        switch (exclusiveFilter) {
          case "reserved":
            facetsFilters.CC_NETWORK.push(`${ALGOLIA_FACETS.EXCLUSIVE}:true`)
            break
          case "partner_faculty_career_center":
            if (facultySchoolId !== NIL_UUID) {
              facetsFilters.CC_NETWORK.push(`${ALGOLIA_FACETS.PARTNER_SCHOOL_IDS}:'${facultySchoolId}'`)
            }
            break
          case "partner_career_center":
            facetsFilters.CC_NETWORK.push(`${ALGOLIA_FACETS.PARTNER_SCHOOL_IDS}:'${schoolId}'`)
            break
          case "created_by_student_or_alumni":
            facetsFilters.CC_NETWORK.push(`${ALGOLIA_FACETS.CREATED_BY_STUDENT_OR_ALUMNI}:true`)
            break
          default:
            break
        }
      })
    }
    if (tags && tags.length > 0) {
      facetsFilters.TAGS = tags.map(tag => {
        const schoolTagFilter = `${ALGOLIA_FACETS.TAGS.replace("<school_uuid>", schoolId)}:'${getEscapedAlgoliaValue(
          tag
        )}'`

        const facultyTagFilter =
          facultySchoolId && facultySchoolId !== ""
            ? `${ALGOLIA_FACETS.TAGS.replace("<school_uuid>", facultySchoolId)}:'${getEscapedAlgoliaValue(tag)}'`
            : ""

        return [schoolTagFilter, facultyTagFilter].filter(Boolean)
      })
    }
  }

  if (workExperiences && workExperiences.length > 0) {
    facetsFilters.WORK_EXPERIENCE_CODE = workExperiences.map(
      workExperience => `${ALGOLIA_FACETS.WORK_EXPERIENCE_CODE}:'${workExperience}'`
    )
  }

  if (remoteTypes && remoteTypes.length > 0) {
    facetsFilters.REMOTE_TYPES = remoteTypes.map(remoteType => `${ALGOLIA_FACETS.REMOTE_TYPES}:'${remoteType}'`)
  }

  if (companyBusinessTypes && companyBusinessTypes.length > 0) {
    facetsFilters.COMPANY_BUSINESS_TYPE = companyBusinessTypes.map(
      companyBusinessType => `${ALGOLIA_FACETS.COMPANY_BUSINESS_TYPE}:'${companyBusinessType}'`
    )
  }

  if (companyIds && companyIds.length > 0) {
    facetsFilters.COMPANY_IDS = companyIds.map(companyId => `${ALGOLIA_FACETS.COMPANY_IDS}:'${companyId}'`)
  }

  if (jobWithImpact) {
    facetsFilters.JOB_WITH_IMPACT = [`${ALGOLIA_FACETS.JOB_WITH_IMPACT}:'true'`]
  }

  if (publicationDate) {
    facetsFilters.PUBLICATION_DATE = getAlgoliaPublicationDateFilter(publicationDate)
  }

  return facetsFilters
}
